import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Auth,
  User,
  fetchSignInMethodsForEmail,
  signInWithPopup,
  user,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import {
  filterUndefined,
  getError,
  shareReplayCold,
  snapshot,
  type IAuthClaims,
  type IUserWorkspace,
} from '@principle-theorem/shared';
import {
  ActionCodeInfo,
  ActionCodeSettings,
  AdditionalUserInfo,
  EmailAuthProvider,
  GoogleAuthProvider,
  IdTokenResult,
  MultiFactorError,
  UserCredential,
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  getAdditionalUserInfo,
  getAuth,
  getIdTokenResult,
  isSignInWithEmailLink,
  linkWithCredential,
  reauthenticateWithCredential,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { duration } from 'moment-timezone';
import { BehaviorSubject, Observable, noop, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AuthFirebaseFunctionsService } from './auth-firebase-functions.service';
import { MultifactorService } from './multifactor/multifactor.service';
import { IMultifactorVerificationContext } from './multifactor/multifactor.types';
import { WorkspaceService } from './workspace.service';

export type AuthUser = User;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _multifactorContextTtlMs = duration(
    5,
    'minutes'
  ).asMilliseconds();
  private _multifactorVerificationRequired$ = new BehaviorSubject<
    IMultifactorVerificationContext | undefined
  >(undefined);
  private _multifactorContextTimeout: ReturnType<typeof setTimeout> | undefined;
  authUser$: Observable<AuthUser | undefined>;
  authToken$: Observable<IdTokenResult | undefined>;
  claims$: Observable<IAuthClaims | undefined>;
  multifactorVerificationRequired$: Observable<
    IMultifactorVerificationContext | undefined
  > = this._multifactorVerificationRequired$.asObservable();

  constructor(
    private _auth: Auth,
    private _workspace: WorkspaceService,
    private _functions: AuthFirebaseFunctionsService,
    private _router: Router,
    private _multifactorService: MultifactorService
  ) {
    this.authUser$ = this._userTokenChanged$().pipe(shareReplayCold());

    this.authToken$ = this.authUser$.pipe(
      switchMap((currentUser) =>
        currentUser ? getIdTokenResult(currentUser) : of(undefined)
      )
    );

    this.claims$ = this.authToken$.pipe(
      map((token) =>
        token ? (token.claims as unknown as IAuthClaims) : undefined
      ),
      distinctUntilChanged(),
      takeUntilDestroyed(),
      shareReplayCold()
    );

    this.authUser$
      .pipe(
        filterUndefined(),
        switchMap(() => this.checkWorkspaceAccess())
      )
      .subscribe(noop);
  }

  async sendPassowrdlessLoginLink(
    email: string,
    appRedirectUrl: string
  ): Promise<void> {
    const auth = getAuth();

    const actionCodeSettings: ActionCodeSettings = {
      url: appRedirectUrl,
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(`Error: ${getError(error)}`);
      throw error;
    }
  }

  async linkToExistingAccount(email: string): Promise<void> {
    const credential = EmailAuthProvider.credentialWithLink(
      email,
      window.location.href
    );

    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('No user is currently signed in.');
      }

      const userCredential = await linkWithCredential(
        auth.currentUser,
        credential
      );
      // eslint-disable-next-line no-console
      console.log(`User linked: ${userCredential.user.email}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error linking account: ${getError(error)}`);
      throw error;
    }
  }

  async reauthenticate(email: string): Promise<void> {
    const credential = EmailAuthProvider.credentialWithLink(
      email,
      window.location.href
    );

    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('No user is currently signed in.');
      }

      const userCredential = await reauthenticateWithCredential(
        auth.currentUser,
        credential
      );

      // eslint-disable-next-line no-console
      console.log(`User reauthenticated: ${userCredential.user.email}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error reauthenticating: ${getError(error)}`);
      throw error;
    }
  }

  async checkWorkspaceAccess(): Promise<void> {
    const workspaces = await this._functions.getUserWorkspaces();
    this._workspace.setWorkspaces(workspaces);

    if (!workspaces.length) {
      return;
    }

    if (workspaces.length === 1) {
      return this._workspace.setWorkspace(workspaces[0]);
    }

    const workspace: string | undefined = await snapshot(
      this._workspace.workspace$
    );

    if (!workspace) {
      return this._workspace.setWorkspace(workspaces[0]);
    }

    const workspaceData: IUserWorkspace | undefined = workspaces.find(
      (currentWorkspace) => currentWorkspace.workspace === workspace
    );
    if (!workspaceData) {
      return this._workspace.setWorkspace(workspaces[0]);
    }

    return this._workspace.setWorkspace(workspaceData);
  }

  async updateClaims(): Promise<void> {
    await this._functions.setAuthClaims();
    await this.loadClaims();
  }

  async logout(): Promise<void> {
    await this._workspace.reset();
    await signOut(this._auth);
    this.clearMultifactorContext();
    await this._router.navigate(['/login']);
  }

  async applyActionCode(code: string): Promise<void> {
    await applyActionCode(this._auth, code);
  }

  async checkActionCode(code: string): Promise<ActionCodeInfo> {
    return checkActionCode(this._auth, code);
  }

  async verifyPasswordCode(code: string): Promise<string> {
    return verifyPasswordResetCode(this._auth, code);
  }

  async confirmPasswordReset(code: string, password: string): Promise<void> {
    await confirmPasswordReset(this._auth, code, password);
  }

  async signIn(
    email: string,
    password: string
  ): Promise<UserCredential | undefined> {
    try {
      return await signInWithEmailAndPassword(this._auth, email, password);
    } catch (error) {
      this._handleMultifactorError(error);
    }
  }

  async signInWithGoogleProvider(): Promise<void> {
    try {
      await signInWithPopup(this._auth, new GoogleAuthProvider());
    } catch (error) {
      this._handleMultifactorError(error);
    }
  }

  clearMultifactorContext(): void {
    if (this._multifactorContextTimeout) {
      clearTimeout(this._multifactorContextTimeout);
      this._multifactorContextTimeout = undefined;
    }
    this._multifactorVerificationRequired$.next(undefined);
  }

  /**
   * Stores the MFA verification context (containing the `MultiFactorResolver`)
   * and schedules an automatic clear after {@link _multifactorContextTtlMs}
   * (5 minutes). Subsequent calls cancel the previous timeout and restart the
   * timer, so the TTL is always relative to the most recent call.
   */
  setMultifactorVerificationContext(
    context: IMultifactorVerificationContext
  ): void {
    if (this._multifactorContextTimeout) {
      clearTimeout(this._multifactorContextTimeout);
    }
    this._multifactorVerificationRequired$.next(context);
    this._multifactorContextTimeout = setTimeout(
      () => this.clearMultifactorContext(),
      this._multifactorContextTtlMs
    );
  }

  isSignInLink(url: string): boolean {
    return isSignInWithEmailLink(this._auth, url);
  }

  async signInWithEmailLink(
    email: string,
    url: string
  ): Promise<AdditionalUserInfo | undefined> {
    const userCredential = await signInWithEmailLink(this._auth, email, url);
    return getAdditionalUserInfo(userCredential) ?? undefined;
  }

  getSignInMethods(email: string): Promise<string[]> {
    return fetchSignInMethodsForEmail(this._auth, email);
  }

  async loadClaims(): Promise<void> {
    const currentUser = await snapshot(user(this._auth));
    if (!currentUser) {
      throw new Error('Not Authenticated');
    }
    await getIdTokenResult(currentUser, true);
  }

  private _userTokenChanged$(): Observable<AuthUser | undefined> {
    return new Observable((subscriber) => {
      const unsubscribe = this._auth.onIdTokenChanged((currentUser) =>
        subscriber.next(currentUser ?? undefined)
      );
      return () => unsubscribe;
    });
  }

  private _handleMultifactorError(error: unknown): void {
    if (
      (error as MultiFactorError).code === 'auth/multi-factor-auth-required'
    ) {
      const context = this._multifactorService.resolveMultiFactorSignIn(
        error as MultiFactorError
      );
      this.setMultifactorVerificationContext(context);
      return;
    }
    throw error;
  }
}
